import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartConfiguration, Plugin } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { DataPlatformService } from '../../../data-platform/data-platform.service';
import {
  DataPlatformAudienceCountDailyPoint,
  DataPlatformAudienceDefinition,
  DataPlatformAudienceRule,
  DataPlatformAudienceStatsRow,
} from '../../../data-platform/data-platform.model';
import { ThemeService } from '../../../shared/services/theme.service';
import { DAILY_SUPPORTED_RULE_TYPES } from '../../../data-platform/audiences/audience-filter-to-rule.utils';
import {
  addDays,
  coversRange,
  definitionBreaks,
  nightlyRows,
  rowsInRange,
  utcDay,
} from '../../../data-platform/audiences/audience-stats.utils';

const DEFAULT_LOOKBACK_DAYS = 14;

/** Offered when nightly rows cover them. */
const SNAPSHOT_RANGE_OPTIONS: readonly number[] = [DEFAULT_LOOKBACK_DAYS, 90, 365];

const BREAK_LINE_COLOR = 'rgba(245, 158, 11, 0.9)';

type TrendSource = 'live' | 'snapshot';

interface RangeChoice {
  days: number;
  label: string;
}

@Component({
  selector: 'app-audience-trend-chart',
  templateUrl: './audience-trend-chart.component.html',
  styleUrls: ['./audience-trend-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AudienceTrendChartComponent implements OnInit, OnChanges {
  /** Audience id (used in the request, returned in the response for sanity). */
  @Input() audienceId!: string;

  /** Site IDs the audience lives under. */
  @Input() siteIds!: number[];

  /**
   * Rules in the new data-platform shape. The component is responsible for
   * mapping these into a DataPlatformAudienceDefinition and calling
   * countAudienceDaily — the parent just provides the rule list.
   */
  @Input() rules: DataPlatformAudienceRule[] = [];

  /** Window size in days. Defaults to 14 to match the "last 2 weeks" design intent. */
  @Input() lookbackDays = DEFAULT_LOOKBACK_DAYS;

  /** `{page}` of the DataPlatform feature tag. */
  @Input({ required: true }) featurePage!: string;

  /** Null while the rules are edited or unsaved. */
  @Input() snapshotAudienceId: string | null = null;

  @Input() set todayCount(count: number | null) {
    this.liveToday.set(count);
  }

  /** Multiplies each day's count; null keeps the counts exact. */
  @Input() set estimateShare(share: number | null) {
    this.share.set(share);
  }

  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly translateService = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal<boolean>(false);
  protected readonly errored = signal<boolean>(false);
  protected readonly rangeDays = signal<number>(DEFAULT_LOOKBACK_DAYS);

  private readonly livePoints = signal<DataPlatformAudienceCountDailyPoint[]>([]);
  private readonly snapshotRows = signal<DataPlatformAudienceStatsRow[]>([]);
  private readonly source = signal<TrendSource>('live');
  private readonly liveToday = signal<number | null>(null);
  private readonly share = signal<number | null>(null);
  private readonly isDark = signal<boolean>(false);
  /** Answers to an older fetch are ignored. */
  private fetchSequence = 0;

  protected readonly fromSnapshot = computed<boolean>(() => this.source() === 'snapshot');

  protected readonly estimated = computed<boolean>(() => this.source() === 'live' && this.share() !== null);

  protected readonly rangeOptions = computed<number[]>(() => {
    if (this.source() !== 'snapshot') {
      return [];
    }
    const today = utcDay(new Date());
    const rows = this.snapshotRows();
    return SNAPSHOT_RANGE_OPTIONS.filter((days) => coversRange(rows, addDays(today, -(days - 1)), today, today));
  });

  protected readonly rangeChoices = computed<RangeChoice[]>(() =>
    this.rangeOptions().map((days) => ({
      days,
      label:
        days === 365
          ? this.translateService.instant('data-platform.trend-range-year')
          : this.translateService.instant('data-platform.trend-range-days', { days }),
    })),
  );

  private readonly rangeRows = computed<DataPlatformAudienceStatsRow[]>(() => {
    const today = utcDay(new Date());
    return rowsInRange(this.snapshotRows(), addDays(today, -(this.rangeDays() - 1)), today);
  });

  protected readonly points = computed<DataPlatformAudienceCountDailyPoint[]>(() => {
    if (this.source() === 'live') {
      return this.livePoints();
    }
    const points = this.rangeRows().map((row) => ({ date: row.date, count: row.members }));
    const today = this.liveToday();
    return today === null ? points : [...points, { date: utcDay(new Date()), count: today }];
  });

  protected readonly breakDates = computed<string[]>(() => {
    if (this.source() !== 'snapshot') {
      return [];
    }
    const rows = this.rangeRows();
    return definitionBreaks(rows).map((index) => rows[index].date);
  });

  private readonly shownPoints = computed<DataPlatformAudienceCountDailyPoint[]>(() => {
    const points = this.points();
    const share = this.share();
    return !this.estimated() || share === null
      ? points
      : points.map((p) => ({ ...p, count: Math.round(p.count * share) }));
  });

  /**
   * True when the audience uses rule types whose semantics don't translate
   * cleanly into the daily aggregation server-side. The chart still renders
   * but with a "best-effort" badge.
   */
  protected readonly bestEffort = computed<boolean>(
    () => !this.fromSnapshot() && this.rules.some((r) => !DAILY_SUPPORTED_RULE_TYPES.has(r.type.toLowerCase())),
  );

  protected readonly latestValue = computed<number | null>(() => {
    const pts = this.shownPoints();
    return pts.length ? pts[pts.length - 1].count : null;
  });

  protected readonly delta = computed<{ percent: number; up: boolean } | null>(() => {
    const pts = this.points();
    if (pts.length < 2) return null;
    const first = pts[0].count;
    const last = pts[pts.length - 1].count;
    if (first === 0) {
      return last === 0 ? { percent: 0, up: false } : { percent: 100, up: true };
    }
    const change = ((last - first) / first) * 100;
    return { percent: Math.abs(Math.round(change * 10) / 10), up: change >= 0 };
  });

  protected readonly chartConfig = computed<{
    data: ChartConfiguration<'line'>['data'];
    options: ChartConfiguration<'line'>['options'];
    plugins: Plugin<'line'>[];
  } | null>(() => {
    const pts = this.shownPoints();
    if (!pts.length) return null;
    const dark = this.isDark();
    const gridColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const textColor = dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
    const lineColor = '#1976d2';
    const breakIndexes = this.breakDates()
      .map((date) => pts.findIndex((p) => p.date === date))
      .filter((index) => index >= 0);
    const rulesChanged: string = this.translateService.instant('data-platform.trend-rules-changed');

    return {
      data: {
        labels: pts.map((p) => this.formatLabel(p.date)),
        datasets: [
          {
            data: pts.map((p) => p.count),
            borderColor: lineColor,
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              afterBody: (items) => (items.some((item) => breakIndexes.includes(item.dataIndex)) ? [rulesChanged] : []),
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 10 },
              color: textColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: { font: { size: 10 }, color: textColor },
          },
        },
        animation: false,
      },
      plugins: breakIndexes.length ? [breakLinesPlugin(breakIndexes)] : [],
    };
  });

  ngOnInit(): void {
    this.themeService.isDarkThemeBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dark) => this.isDark.set(dark ?? false));
    this.fetchTrend();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ngOnInit does the first fetch.
    const refetchInputs = ['audienceId', 'rules', 'siteIds', 'lookbackDays', 'snapshotAudienceId'];
    if (refetchInputs.some((input) => changes[input]?.firstChange === false)) {
      this.fetchTrend();
    }
  }

  protected retry(): void {
    this.fetchTrend();
  }

  protected selectRange(days: number): void {
    this.rangeDays.set(days);
  }

  private fetchTrend(): void {
    if (!this.audienceId || !this.siteIds?.length) return;
    const snapshotId = this.snapshotAudienceId;
    if (!snapshotId) {
      this.fetchLive();
      return;
    }

    const today = utcDay(new Date());
    const longest = Math.max(...SNAPSHOT_RANGE_OPTIONS);
    const request = ++this.fetchSequence;
    this.loading.set(true);
    this.errored.set(false);
    this.dataPlatformService
      .getAudienceStats(
        { siteIds: this.siteIds, audienceIds: [snapshotId], from: addDays(today, -(longest - 1)), to: today },
        `${this.featurePage}-trend-snapshot`,
      )
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (request !== this.fetchSequence) {
          return;
        }
        const rows = result?.isPositive() && result.data ? result.data.rows : [];
        const nightly = nightlyRows(rows).filter((row) => row.date < today);
        if (!coversRange(nightly, addDays(today, -(this.lookbackDays - 1)), today, today)) {
          this.fetchLive();
          return;
        }
        this.snapshotRows.set(nightly);
        this.source.set('snapshot');
        const options = this.rangeOptions();
        if (!options.includes(this.rangeDays())) {
          this.rangeDays.set(options[0]);
        }
        this.loading.set(false);
      });
  }

  private fetchLive(): void {
    const request = ++this.fetchSequence;
    this.source.set('live');
    this.rangeDays.set(this.lookbackDays);
    if (!this.rules?.length) {
      // No rules → nothing to count. Render empty without hitting the server.
      this.loading.set(false);
      this.livePoints.set([]);
      return;
    }

    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - (this.lookbackDays - 1));

    const audience: DataPlatformAudienceDefinition = {
      id: this.audienceId,
      rules: this.rules,
    };

    this.loading.set(true);
    this.errored.set(false);
    this.dataPlatformService
      .countAudienceDaily(
        {
          siteIds: this.siteIds,
          audience,
          from: toIsoDate(start),
          to: toIsoDate(today),
        },
        `${this.featurePage}-trend-chart`,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (request !== this.fetchSequence) {
            return;
          }
          this.loading.set(false);
          if (result?.isPositive() && result.data) {
            this.livePoints.set(result.data.points ?? []);
          } else {
            this.errored.set(true);
            this.livePoints.set([]);
          }
        },
        error: () => {
          if (request !== this.fetchSequence) {
            return;
          }
          this.loading.set(false);
          this.errored.set(true);
          this.livePoints.set([]);
        },
      });
  }

  private formatLabel(isoDate: string): string {
    const date = new Date(isoDate + 'T00:00:00');
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }
    const months: string[] = this.translateService.instant('time.months');
    return `${date.getDate()} ${months[date.getMonth()]}`;
  }
}

function breakLinesPlugin(indexes: readonly number[]): Plugin<'line'> {
  return {
    id: 'audienceRuleBreaks',
    afterDatasetsDraw: (chart) => {
      const meta = chart.getDatasetMeta(0);
      const { top, bottom } = chart.chartArea;
      const context = chart.ctx;
      context.save();
      context.setLineDash([4, 4]);
      context.strokeStyle = BREAK_LINE_COLOR;
      context.lineWidth = 1;
      for (const index of indexes) {
        const x = meta.data[index]?.x;
        if (x === undefined) {
          continue;
        }
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.stroke();
      }
      context.restore();
    },
  };
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
